import { CollisionEdges, PopoverPositions, PositioningShorthand } from "./popover.options.js";
import { getBoundaryBounds, getAvailableSpace, detectCollisionEdgeFromIntersection, DEFAULT_ROOT_MARGIN } from "../../util/positioning.js";
export class Positioning {
    set repositionMode(value) {
        this._repositionMode = value;
    }
    get repositionMode() {
        return this._repositionMode;
    }
    set popoverReference(value) {
        this._popoverReference = value;
    }
    get popoverReference() {
        return this._popoverReference;
    }
    set position(value) {
        this._position = value;
    }
    get position() {
        return this._position;
    }
    get openPositions() {
        return this.getOpenPositions();
    }
    constructor(args) {
        this.rootMargin = DEFAULT_ROOT_MARGIN;
        this.overflowBoundaryReference = null;
        this._position = PositioningShorthand.aboveEnd;
        this._openPositions = [];
        /**
         *
         * checkPosition
         *
         * This method runs when the popover has been repositioned and checks if the new position has space available for the popover (since we don't want to move to a position where there isn't enough space).
         *
         * Returns true if the position has space available for the popover, otherwise returns false.
         */
        this.checkPosition = (openPosition) => {
            var _a, _b, _c;
            const popoverReferenceRect = (_a = this._popoverReference) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            const anchorRect = (_b = this.anchorReference) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
            if (popoverReferenceRect && anchorRect) {
                const boundary = getBoundaryBounds(this.overflowBoundaryReference);
                const space = getAvailableSpace(anchorRect, boundary, this.rootMargin);
                // Primary direction checks
                const above = popoverReferenceRect.height < space.above;
                const below = popoverReferenceRect.height < space.below;
                const start = popoverReferenceRect.width < space.start;
                const end = popoverReferenceRect.width < space.end;
                // Secondary alignment checks (for above/below positions)
                const halfOverhangWidth = (popoverReferenceRect.width - anchorRect.width) / 2;
                const center = halfOverhangWidth < space.start && halfOverhangWidth < space.end;
                const aboveBelowEnd = popoverReferenceRect.width < space.start + anchorRect.width &&
                    space.end > 0;
                const aboveBelowStart = popoverReferenceRect.width < space.end + anchorRect.width &&
                    space.start > 0;
                // Secondary alignment checks (for start/end positions)
                const halfOverhangHeight = (popoverReferenceRect.height - anchorRect.height) / 2;
                const startEndMiddle = halfOverhangHeight < space.above && halfOverhangHeight < space.below;
                const startEndBottom = popoverReferenceRect.height - anchorRect.height < space.above;
                const startEndTop = space.above > 0;
                // Position fit checks: [primaryDirectionFits, secondaryAlignmentFits]
                const positionChecks = {
                    [PositioningShorthand.aboveStart]: above && aboveBelowStart,
                    [PositioningShorthand.aboveCenter]: above && center,
                    [PositioningShorthand.aboveEnd]: above && aboveBelowEnd,
                    [PositioningShorthand.belowStart]: below && aboveBelowStart,
                    [PositioningShorthand.belowCenter]: below && center,
                    [PositioningShorthand.belowEnd]: below && aboveBelowEnd,
                    [PositioningShorthand.startTop]: start && startEndTop,
                    [PositioningShorthand.startMiddle]: start && startEndMiddle,
                    [PositioningShorthand.startBottom]: start && startEndBottom,
                    [PositioningShorthand.endTop]: end && startEndTop,
                    [PositioningShorthand.endMiddle]: end && startEndMiddle,
                    [PositioningShorthand.endBottom]: end && startEndBottom
                };
                return (_c = positionChecks[openPosition]) !== null && _c !== void 0 ? _c : false;
            }
            return false;
        };
        /**
         * ## getOpenPositions
         *
         * @description Returns an array of open positions based on the repositionMode
         */
        this.getOpenPositions = () => {
            this._openPositions = PopoverPositions.filter((position) => this.checkPosition(position));
            return this._openPositions;
        };
        /**
         * ## oppositePosition
         * Returns the opposite position of the collisionEdge
         */
        this.oppositePosition = (collisionEdge) => {
            switch (collisionEdge) {
                case CollisionEdges.top:
                    if (this.position === PositioningShorthand.aboveStart) {
                        return [PositioningShorthand.belowStart];
                    }
                    if (this.position === PositioningShorthand.aboveCenter) {
                        return [PositioningShorthand.belowCenter];
                    }
                    if (this.position === PositioningShorthand.aboveEnd) {
                        return [PositioningShorthand.belowEnd];
                    }
                    else {
                        return [PositioningShorthand.belowCenter];
                    }
                case CollisionEdges.right:
                    if (this.position === PositioningShorthand.endBottom) {
                        return [PositioningShorthand.startBottom];
                    }
                    if (this.position === PositioningShorthand.endMiddle) {
                        return [PositioningShorthand.startMiddle];
                    }
                    if (this.position === PositioningShorthand.endTop) {
                        return [PositioningShorthand.startTop];
                    }
                    else {
                        return [PositioningShorthand.startMiddle];
                    }
                case CollisionEdges.bottom:
                    if (this.position === PositioningShorthand.belowStart) {
                        return [PositioningShorthand.aboveStart];
                    }
                    if (this.position === PositioningShorthand.belowCenter) {
                        return [PositioningShorthand.aboveCenter];
                    }
                    if (this.position === PositioningShorthand.belowEnd) {
                        return [PositioningShorthand.aboveEnd];
                    }
                    else {
                        return [PositioningShorthand.aboveCenter];
                    }
                case CollisionEdges.left:
                    if (this.position === PositioningShorthand.startTop) {
                        return [PositioningShorthand.endTop];
                    }
                    if (this.position === PositioningShorthand.startMiddle) {
                        return [PositioningShorthand.endMiddle];
                    }
                    if (this.position === PositioningShorthand.startBottom) {
                        return [PositioningShorthand.endBottom];
                    }
                    else {
                        return [PositioningShorthand.endMiddle];
                    }
            }
        };
        /*
         * ## getOpenPositionsFromEdge
         *
         * Returns an array of open positions based on the collisionEdge
         */
        this.getOpenPositionsFromEdge = (collisionEdge) => {
            if (this.repositionMode === "auto") {
                return PopoverPositions.filter((position) => this.checkPosition(position));
            }
            else if (this.repositionMode === "flip" && collisionEdge) {
                // For flip mode, get the opposite position and verify it has space
                const oppositePositions = this.oppositePosition(collisionEdge);
                const validPositions = oppositePositions.filter((position) => this.checkPosition(position));
                // Return valid opposite positions, or fall back to current position
                return validPositions.length > 0 ? validPositions : [this.position];
            }
            else if (collisionEdge) {
                return this.oppositePosition(collisionEdge);
            }
            else {
                return [this.position];
            }
        };
        /**
         * ## findOpenPositionsFromCollision
         *
         * Returns an array of open positions based on the collisionEdge
         */
        this.findOpenPositionsFromCollision = (entry) => {
            const intersectingEdge = detectCollisionEdgeFromIntersection(entry);
            return this.getOpenPositionsFromEdge(intersectingEdge);
        };
        const { repositionMode, popoverReference, anchorReference, overflowBoundaryReference } = args;
        this._repositionMode = repositionMode;
        this._popoverReference = popoverReference;
        this.anchorReference = anchorReference;
        if (overflowBoundaryReference) {
            this.overflowBoundaryReference = overflowBoundaryReference;
        }
    }
}
//# sourceMappingURL=positioning.js.map